"use client"

import { useState, useEffect, useRef } from "react"
import {
  Box,
  Grid,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  CircularProgress,
} from "@mui/material"
import {
  DateRange as DateRangeIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { useNotification } from "../../contexts/NotificationContext"
import PageHeader from "../../components/common/PageHeader"
import LoadingSpinner from "../../components/common/LoadingSpinner"
import ErrorDisplay from "../../components/common/ErrorDisplay"
import EmptyState from "../../components/common/EmptyState"
import { getCreditUsageByOrganization, getCreditUsageByPeriod, getCreditUsageByOrganizationPaginated } from "../../services/credit.service"
import InfiniteScroll from 'react-infinite-scroll-component';
// Removed getCustomers import - not needed as customer data comes from usage API
import { motion, AnimatePresence } from "framer-motion"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts"

const CreditUsage = () => {
  const { selectedOrganization, user } = useAuth()
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const theme = useTheme()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [usageData, setUsageData] = useState([])
  // Removed customers state - customer data comes from usage API
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const limit = 20
  const [isFetching, setIsFetching] = useState(false)
  const [noMoreData, setNoMoreData] = useState(false)
  const [showLoadMore, setShowLoadMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const searchTimeoutRef = useRef(null)
  const [selectedCustomer, setSelectedCustomer] = useState("all")
  const [selectedPeriod, setSelectedPeriod] = useState("month")
  const [chartData, setChartData] = useState([])

  useEffect(() => {
    const orgId = selectedOrganization?._id || user?.organizationId
    if (orgId) {
      setUsageData([]);
      setPage(1);
      setHasMore(true);
      fetchUsagePaginated(orgId, 1, true);
    } else {
      setLoading(false)
      setError("No organization selected. Please select an organization to view credit usage.")
    }
  }, [selectedOrganization, user, selectedPeriod, selectedCustomer])

  const fetchUsagePaginated = async (orgId, pageToFetch = 1, reset = false, query = "") => {
    setIsFetching(true);
    setLoading(true);
    try {
      // Customer data is now included in the usage API response

      const params = { organizationId: orgId, page: pageToFetch, limit};
      if(query) {
        params.search = query;
      }
      
      const response = await getCreditUsageByOrganizationPaginated(params);
      let newUsageData = response.data || [];
      setTotalCount(response.count || 0);
      
      if (reset) {
        setUsageData(newUsageData);
        setNoMoreData(false);
        setShowLoadMore(false);
        // Prepare chart data
        prepareChartData(newUsageData)
      } else {
        setUsageData((prev) => {
          const combined = [...prev, ...newUsageData];
          return combined;
        });
      }
      
      if (newUsageData.length < limit) {
        setNoMoreData(true);
        setShowLoadMore(true);
      } else {
        setNoMoreData(false);
        setShowLoadMore(false);
      }
      setHasMore(!showLoadMore);
    } catch (error) {
      console.error("Failed to fetch data:", error)
      setError(error.message || "Failed to load credit usage data")
      // Initialize with empty arrays to prevent filter errors
      setUsageData([])
      setChartData([])
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const fetchNextPage = () => {
    if (isFetching || showLoadMore) return;
    setPage((prevPage) => {
      let nextPage = prevPage;
      if(totalCount >= limit) {
        nextPage = prevPage + 1;
      }
      const orgId = selectedOrganization?._id || user?.organizationId
      fetchUsagePaginated(orgId, nextPage, false, searchTerm);
      return nextPage;
    });
  };

  const handleLoadMore = () => {
    setShowLoadMore(false);
    setPage((prevPage) => {
      let nextPage = prevPage;
      if(totalCount >= limit) {
        nextPage = prevPage + 1;
      }
      const orgId = selectedOrganization?._id || user?.organizationId
      fetchUsagePaginated(orgId, nextPage, false, searchTerm);
      return nextPage;
    });
  };

  const prepareChartData = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      setChartData([])
      return
    }

    // Group data by date
    const groupedByDate = data.reduce((acc, item) => {
      const date = new Date(item.createdAt || item.date).toLocaleDateString()
      if (!acc[date]) {
        acc[date] = 0
      }
      acc[date] += item.amount
      return acc
    }, {})

    // Convert to chart format
    const chartData = Object.keys(groupedByDate).map((date) => ({
      date,
      credits: groupedByDate[date],
    }))

    // Sort by date
    chartData.sort((a, b) => new Date(a.date) - new Date(b.date))

    setChartData(chartData)
  }

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchTerm(query);

    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setUsageData([]);
      setNoMoreData(false);
      setShowLoadMore(false);

      const orgId = selectedOrganization?._id || user?.organizationId
      fetchUsagePaginated(orgId, 1, true, query);
    }, 500);
  }

  const handleCustomerChange = (event) => {
    setSelectedCustomer(event.target.value)
    setPage(1);
    setUsageData([]);
    setNoMoreData(false);
    setShowLoadMore(false);
    
    const orgId = selectedOrganization?._id || user?.organizationId
    fetchUsagePaginated(orgId, 1, true, searchTerm);
  }

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value)
    setPage(1);
    setUsageData([]);
    setNoMoreData(false);
    setShowLoadMore(false);
    
    const orgId = selectedOrganization?._id || user?.organizationId
    fetchUsagePaginated(orgId, 1, true, searchTerm);
  }

  const handleExportData = () => {
    // Implementation for exporting data
    showNotification("Export functionality will be implemented soon", "info")
  }

  // Note: Search is now handled server-side, no need for client-side filtering

  // Calculate total usage - ensure usageData is an array
  const totalUsage = Array.isArray(usageData) ? usageData.reduce((sum, item) => sum + (item.amount || 0), 0) : 0

  if (error) {
    return (
      <ErrorDisplay message="There was a problem loading the credit usage data." details={error} onRetry={() => {
        const orgId = selectedOrganization?._id || user?.organizationId
        fetchUsagePaginated(orgId, 1, true, searchTerm);
      }} />
    )
  }

  return (
    <Box>
      <PageHeader
        title="Credit Usage"
        subtitle="Track and analyze credit usage across your organization"
        breadcrumbs={[{ text: "Credits", link: "/credits" }, { text: "Usage" }]}
        action={true}
        actionText="Export Data"
        actionIcon={<DownloadIcon />}
        onActionClick={handleExportData}
      />

      {/* Filters */}
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          border: "1px solid rgba(0, 0, 0, 0.05)",
          background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Customer"
              value={selectedCustomer}
              onChange={handleCustomerChange}
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 2,
                },
              }}
            >
              <MenuItem value="all">All Customers</MenuItem>
              {Array.from(new Set(usageData.map(usage => {
                const customerId = typeof usage.customerId === 'object' ? usage.customerId._id : usage.customerId
                return customerId
              })))
                .filter(customerId => customerId)
                .map(customerId => {
                  const usage = usageData.find(u => {
                    const uCustomerId = typeof u.customerId === 'object' ? u.customerId._id : u.customerId
                    return uCustomerId === customerId
                  })
                  const customer = typeof usage?.customerId === 'object' ? usage.customerId : usage?.customer
                  const customerName = customer?.name || "Unknown Customer"
                  return (
                    <MenuItem key={customerId} value={customerId}>
                      {customerName}
                    </MenuItem>
                  )
                })}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Time Period"
              value={selectedPeriod}
              onChange={handlePeriodChange}
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 2,
                },
              }}
            >
              <MenuItem value="day">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search usage data..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                sx: {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Usage Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Credit Usage Over Time
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  icon={<DateRangeIcon />}
                  label={
                    selectedPeriod === "day"
                      ? "Today"
                      : selectedPeriod === "week"
                        ? "This Week"
                        : selectedPeriod === "month"
                          ? "This Month"
                          : selectedPeriod === "year"
                            ? "This Year"
                            : "All Time"
                  }
                  color="primary"
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                />
                <Chip
                  icon={<FilterIcon />}
                  label={
                    selectedCustomer === "all"
                      ? "All Customers"
                      : (() => {
                          const usage = usageData.find((u) => {
                            const uCustomerId = typeof u.customerId === 'object' ? u.customerId._id : u.customerId
                            return uCustomerId === selectedCustomer
                          })
                          const customer = typeof usage?.customerId === 'object' ? usage.customerId : usage?.customer
                          return customer?.name || "Unknown Customer"
                        })()
                  }
                  color="primary"
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                />
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {chartData.length === 0 && loading ? (
              <LoadingSpinner message="Loading credit usage data..." />
            ) : chartData.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                  No usage data available for the selected filters.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip
                      formatter={(value) => [`${value} credits`, "Usage"]}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend />
                    <Bar
                      dataKey="credits"
                      name="Credits Used"
                      fill={theme.palette.primary.main}
                      radius={[4, 4, 0, 0]}
                      animationDuration={1500}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Usage Details */}
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        sx={{
          p: 3,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          border: "1px solid rgba(0, 0, 0, 0.05)",
          background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Usage Details
          </Typography>
          <Typography variant="h6" fontWeight={600} color="primary.main">
            Total: {totalUsage.toLocaleString()} credits
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <AnimatePresence mode="wait">
          {usageData.length === 0 ? (
            loading ? (
              <LoadingSpinner message="Loading credit usage data..." />
            ) : (
              <EmptyState
                title="No Usage Data"
                description="There is no credit usage data available for the selected filters."
                icon={<DateRangeIcon sx={{ fontSize: 60 }} />}
                actionText="Change Filters"
                actionIcon={<FilterIcon />}
                onActionClick={() => setSelectedPeriod("all")}
              />
            )
          ) : (
            <Box id="credit-usage-table-scrollable" sx={{ height: 600, overflow: 'auto' }}>
              <InfiniteScroll
                dataLength={usageData.length}
                next={fetchNextPage}
                hasMore={!showLoadMore}
                loader={isFetching ? (<Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>) : null}
                endMessage={
                  showLoadMore && (
                    <Box sx={{ textAlign: 'center', my: 2, color: 'text.secondary' }}>
                      <b>No more usage data to load.</b>
                      <Box sx={{ mt: 2 }}>
                        <button onClick={handleLoadMore} style={{ padding: '8px 16px', borderRadius: 4, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer' }}>
                          Load More
                        </button>
                      </Box>
                    </Box>
                  )
                }
                scrollableTarget="credit-usage-table-scrollable"
                scrollThreshold={0.98}
              >
                <TableContainer sx={{ borderRadius: 2, overflow: "hidden" }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: "rgba(99, 102, 241, 0.04)" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Customer</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Feature</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="right">
                          Credits Used
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="center">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usageData.map((usage, index) => {
                        const customer = typeof usage.customerId === 'object' ? usage.customerId : usage.customer
                        return (
                          <TableRow
                            key={usage._id || index}
                            component={motion.tr}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                              transition: "all 0.2s",
                              "&:hover": {
                                backgroundColor: "rgba(99, 102, 241, 0.04)",
                              },
                            }}
                          >
                            <TableCell>{new Date(usage.createdAt || usage.date).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {customer?.name || "Unknown Customer"}
                              </Typography>
                            </TableCell>
                            <TableCell>{usage.description || "—"}</TableCell>
                            <TableCell>
                              {usage.feature ? (
                                <Chip
                                  label={usage.feature}
                                  size="small"
                                  sx={{
                                    fontWeight: 500,
                                    backgroundColor: "rgba(99, 102, 241, 0.1)",
                                    color: "primary.main",
                                  }}
                                />
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {(usage.amount || 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="View Customer">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => navigate(`/credits/customer/${usage.customerId}`)}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "rgba(99, 102, 241, 0.1)",
                                    },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfiniteScroll>
            </Box>
          )}
        </AnimatePresence>
      </Paper>
    </Box>
  )
}

export default CreditUsage

